// components/Survey/TransferSurvey.jsx
import React, { useState, useEffect } from 'react';
import { transferSurveyService } from '../../services/surveyService';
import { 
  DocumentDuplicateIcon, 
  BuildingOfficeIcon, 
  ChartBarIcon, 
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';

const TransferSurvey = () => {
  const [companies, setCompanies] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // New state for question selection
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await transferSurveyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      showMessage('Failed to load companies', 'error');
    }
  };

  const handleCompanyChange = async (companyId) => {
    setSelectedCompany(companyId);
    setSelectedSurvey('');
    setSurveyDetails(null);
    setSelectedQuestions({});
    setExpandedCategories({});
    
    if (companyId) {
      setLoading(true);
      try {
        const data = await transferSurveyService.getSurveysByCompany(companyId);
        setSurveys(data);
      } catch (error) {
        showMessage('Failed to load surveys', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSurveyChange = async (surveyId) => {
    setSelectedSurvey(surveyId);
    setSelectedQuestions({});
    setExpandedCategories({});
    setSelectAll(false);
    
    if (surveyId) {
      setLoading(true);
      try {
        const details = await transferSurveyService.getSurveyDetails(surveyId);
        setSurveyDetails(details);
        
        // Initialize all questions as selected by default
        const initialSelection = {};
        details.categories.forEach(category => {
          category.questions.forEach(question => {
            initialSelection[question.id] = true;
          });
        });
        setSelectedQuestions(initialSelection);
        
        // Expand all categories by default
        const initialExpanded = {};
        details.categories.forEach(category => {
          initialExpanded[category.id] = true;
        });
        setExpandedCategories(initialExpanded);
        
      } catch (error) {
        showMessage('Failed to load survey details', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Question selection handlers
  const handleQuestionSelect = (questionId) => {
    setSelectedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handleCategorySelect = (categoryId) => {
    const category = surveyDetails.categories.find(c => c.id === categoryId);
    if (!category) return;

    const allSelected = category.questions.every(q => selectedQuestions[q.id]);
    
    const updatedSelection = { ...selectedQuestions };
    category.questions.forEach(question => {
      updatedSelection[question.id] = !allSelected;
    });
    
    setSelectedQuestions(updatedSelection);
  };

  const handleSelectAll = () => {
    const allQuestionIds = {};
    surveyDetails.categories.forEach(category => {
      category.questions.forEach(question => {
        allQuestionIds[question.id] = !selectAll;
      });
    });
    
    setSelectedQuestions(allQuestionIds);
    setSelectAll(!selectAll);
  };

  const handleCategoryToggle = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleExpandAll = () => {
    const allExpanded = {};
    surveyDetails.categories.forEach(category => {
      allExpanded[category.id] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedCategories({});
  };

  const getSelectedQuestionCount = () => {
    return Object.values(selectedQuestions).filter(Boolean).length;
  };

  const getTotalQuestionCount = () => {
    if (!surveyDetails) return 0;
    return surveyDetails.categories.reduce((total, category) => total + category.questions.length, 0);
  };

  const handleTransfer = async () => {
    if (!selectedCompany || !selectedSurvey || !targetCompany) {
      showMessage('Please select source company, survey, and target company', 'error');
      return;
    }

    const selectedCount = getSelectedQuestionCount();
    if (selectedCount === 0) {
      showMessage('Please select at least one question to transfer', 'error');
      return;
    }

    setTransferring(true);
    try {
      const result = await transferSurveyService.transferSurvey({
        sourceCompanyId: parseInt(selectedCompany),
        sourceSurveyId: parseInt(selectedSurvey),
        targetCompanyId: parseInt(targetCompany),
        selectedQuestionIds: Object.keys(selectedQuestions).filter(id => selectedQuestions[id]).map(id => parseInt(id))
      });

      if (result.success) {
        showMessage(result.message, 'success');
        // Reset form
        setSelectedCompany('');
        setSelectedSurvey('');
        setTargetCompany('');
        setSurveyDetails(null);
        setSurveys([]);
        setSelectedQuestions({});
        setExpandedCategories({});
        setSelectAll(false);
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      showMessage(error.message || 'Transfer failed', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const getQuestionTypeText = (type) => {
    const types = {
      0: 'Text',
      1: 'Multiple Choice',
      2: 'Rating',
      3: 'File Upload',
      4: 'Yes/No',
      5: 'Dropdown',
      6: 'Date',
      7: 'Time',
      8: 'Scale'
    };
    return types[type] || 'Unknown';
  };

  const getQuestionTypeColor = (type) => {
    const colors = {
      0: 'bg-blue-100 text-blue-800',
      1: 'bg-purple-100 text-purple-800',
      2: 'bg-green-100 text-green-800',
      3: 'bg-orange-100 text-orange-800',
      4: 'bg-red-100 text-red-800',
      5: 'bg-indigo-100 text-indigo-800',
      6: 'bg-cyan-100 text-cyan-800',
      7: 'bg-amber-100 text-amber-800',
      8: 'bg-lime-100 text-lime-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-blue-100 via-white to-indigo-100 rounded-2xl shadow-md p-6 border border-gray-100 h-fit">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <DocumentDuplicateIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Transfer Survey</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Select specific questions to transfer between companies
          </p>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 space-y-3">
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600">Total Companies</span>
            <span className="font-semibold text-blue-600">{companies.length}</span>
          </div>
          
          {surveyDetails && (
            <>
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                <span className="text-sm text-gray-600">Categories</span>
                <span className="font-semibold text-green-600">{surveyDetails.categories.length}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                <span className="text-sm text-gray-600">Total Questions</span>
                <span className="font-semibold text-orange-600">
                  {getTotalQuestionCount()}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                <span className="text-sm text-gray-600">Selected Questions</span>
                <span className="font-semibold text-purple-600">
                  {getSelectedQuestionCount()}
                </span>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="text-sm text-green-700 font-medium">
                  Selection Progress
                </div>
                <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(getSelectedQuestionCount() / getTotalQuestionCount()) * 100}%` 
                    }}
                  />
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {getSelectedQuestionCount()} of {getTotalQuestionCount()} questions selected
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Content Panel */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-sm p-6 border border-blue-100"
      >
        {message.text && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <div className="flex items-center">
              <CheckCircleIcon className={`w-5 h-5 mr-2 ${
                message.type === 'success' ? 'text-green-500' : 'text-red-500'
              }`} />
              {message.text}
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Selective Survey Transfer</h1>
          <p className="text-gray-600">Choose specific questions to transfer between companies</p>
        </div>

        {/* Transfer Form */}
        <div className="space-y-6">
          {/* Source Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-500" />
              Source Selection
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Source Company *
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800"
                >
                  <option value="">Choose a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.industry})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Survey *
                </label>
                <select
                  value={selectedSurvey}
                  onChange={(e) => handleSurveyChange(e.target.value)}
                  disabled={!selectedCompany || loading}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800 disabled:opacity-50"
                >
                  <option value="">Choose a survey</option>
                  {surveys.map(survey => (
                    <option key={survey.id} value={survey.id}>
                      {survey.name} ({survey.questionCount} questions)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Target Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-green-500" />
              Target Company
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Target Company *
              </label>
              <select
                value={targetCompany}
                onChange={(e) => setTargetCompany(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none shadow-sm text-gray-800"
              >
                <option value="">Choose target company</option>
                {companies
                  .filter(company => company.id.toString() !== selectedCompany)
                  .map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.industry})
                    </option>
                  ))
                }
              </select>
            </div>
          </div>

          {/* Question Selection Panel */}
          {surveyDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Question Selection</h3>
                  <p className="text-gray-600 text-sm">
                    Select specific questions to transfer ({getSelectedQuestionCount()}/{getTotalQuestionCount()} selected)
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                  >
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={handleExpandAll}
                    className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                  >
                    Expand All
                  </button>
                  <button
                    onClick={handleCollapseAll}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Survey Header */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 text-lg">{surveyDetails.name}</h4>
                {surveyDetails.description && (
                  <p className="text-blue-600 mt-1">{surveyDetails.description}</p>
                )}
              </div>

              {/* Categories and Questions */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {surveyDetails.categories.map((category, catIndex) => {
                  const categoryQuestions = category.questions || [];
                  const selectedInCategory = categoryQuestions.filter(q => selectedQuestions[q.id]).length;
                  const allSelected = categoryQuestions.length > 0 && selectedInCategory === categoryQuestions.length;
                  const someSelected = selectedInCategory > 0 && selectedInCategory < categoryQuestions.length;
                  
                  return (
                    <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div className="bg-gray-50 p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleCategorySelect(category.id)}
                              className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                                allSelected 
                                  ? 'bg-blue-500 border-blue-500 text-white' 
                                  : someSelected
                                  ? 'bg-blue-200 border-blue-300 text-blue-500'
                                  : 'border-gray-300 hover:border-blue-400'
                              }`}
                            >
                              {allSelected && <CheckIcon className="w-3 h-3" />}
                              {someSelected && <div className="w-2 h-2 bg-blue-500 rounded-sm" />}
                            </button>
                            
                            <div>
                              <h5 className="font-semibold text-gray-800">
                                {catIndex + 1}. {category.name}
                              </h5>
                              {category.description && (
                                <p className="text-gray-600 text-sm mt-1">{category.description}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">
                              {selectedInCategory}/{categoryQuestions.length} selected
                            </span>
                            
                            <button
                              onClick={() => handleCategoryToggle(category.id)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              {expandedCategories[category.id] ? (
                                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                              ) : (
                                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Questions List */}
                      <AnimatePresence>
                        {expandedCategories[category.id] && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white"
                          >
                            <div className="p-4 space-y-3">
                              {categoryQuestions.map((question, qIndex) => (
                                <div
                                  key={question.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                                    selectedQuestions[question.id]
                                      ? 'bg-blue-50 border-blue-200'
                                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                  }`}
                                >
                                  <button
                                    onClick={() => handleQuestionSelect(question.id)}
                                    className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors mt-1 ${
                                      selectedQuestions[question.id]
                                        ? 'bg-blue-500 border-blue-500 text-white'
                                        : 'border-gray-300 hover:border-blue-400'
                                    }`}
                                  >
                                    {selectedQuestions[question.id] && <CheckIcon className="w-3 h-3" />}
                                  </button>
                                  
                                  <div className="flex-1">
                                    <div className="flex justify-between items-start gap-4">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                                            {qIndex + 1}
                                          </span>
                                          <span className="text-sm font-medium text-gray-700">
                                            {question.questionText}
                                          </span>
                                        </div>
                                        
                                        {question.helpText && (
                                          <p className="text-xs text-gray-500 mt-1">{question.helpText}</p>
                                        )}
                                      </div>
                                      
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className={`text-xs px-2 py-1 rounded ${getQuestionTypeColor(question.type)}`}>
                                          {getQuestionTypeText(question.type)}
                                        </span>
                                        {question.isRequired && (
                                          <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">
                                            Required
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Question Options Preview */}
                                    {question.optionsJson && (
                                      <div className="mt-2">
                                        <div className="text-xs text-gray-500 mb-1">Options:</div>
                                        <div className="flex flex-wrap gap-1">
                                          {JSON.parse(question.optionsJson).slice(0, 3).map((option, optIndex) => (
                                            <span
                                              key={optIndex}
                                              className="text-xs bg-white border border-gray-300 px-2 py-1 rounded"
                                            >
                                              {option}
                                            </span>
                                          ))}
                                          {JSON.parse(question.optionsJson).length > 3 && (
                                            <span className="text-xs text-gray-500 px-2 py-1">
                                              +{JSON.parse(question.optionsJson).length - 3} more
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Transfer Action */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Ready to Transfer</h3>
                <p className="text-gray-600 text-sm">
                  {surveyDetails ? 
                    `Transferring ${getSelectedQuestionCount()} selected questions to target company` :
                    'Select questions to transfer'
                  }
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {surveyDetails && (
                  <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                    {getSelectedQuestionCount()}/{getTotalQuestionCount()} questions
                  </div>
                )}
                
                <button
                  onClick={handleTransfer}
                  disabled={!selectedCompany || !selectedSurvey || !targetCompany || transferring || getSelectedQuestionCount() === 0}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
                >
                  {transferring ? (
                    <>
                      <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                      Transferring...
                    </>
                  ) : (
                    <>
                      <DocumentDuplicateIcon className="w-5 h-5" />
                      Transfer Selected Questions
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TransferSurvey;